/**
 * AnimatedWaveform — equalizer-style waveform with TWO modes:
 *
 *   1. CSS mode (default) — 32 bars cycle through randomised heights via
 *      `animation-delay` staggering. Zero-cost, always works.
 *
 *   2. Reactive mode — when `levels` (Uint8Array, length === bars) is
 *      supplied, each bar's height is driven by the live FFT amplitude of
 *      the audio element. Bass-weighted, GPU-friendly transforms only.
 *
 * Variants:
 *   accent="gold"   → All-Genre / general (#FFDA33)
 *   accent="cyan"   → Music Radio (#00CCFF)
 *   accent="pink"   → AI Radio  (#FF66AA)
 *   accent="green"  → Beats Radio (#00ff88)
 */
import './AnimatedWaveform.css';

const ACCENTS = {
  gold: '#FFDA33',
  cyan: '#00CCFF',
  pink: '#FF66AA',
  green: '#00ff88',
};

export default function AnimatedWaveform({
  playing = false,
  accent = 'gold',
  bars = 32,
  height = 36,
  className = '',
  testid = 'animated-waveform',
  levels = null,
}) {
  const color = ACCENTS[accent] || ACCENTS.gold;
  const reactive = !!(levels && levels.length === bars);

  return (
    <div
      className={`awv-root ${reactive ? 'awv-reactive' : ''} ${playing ? 'awv-playing' : 'awv-idle'} ${className}`}
      style={{ '--awv-accent': color, height }}
      data-testid={testid}
      aria-hidden="true"
    >
      {Array.from({ length: bars }).map((_, i) => {
        if (reactive) {
          // Map 0–255 amplitude to 8–100% scale Y. We use scaleY (transform)
          // not height so the animation runs on the GPU compositor.
          const a = (levels[i] || 0) / 255;
          const scale = 0.08 + a * 0.92;
          return (
            <span
              key={i}
              className="awv-bar awv-bar-reactive"
              style={{ transform: `scaleY(${scale.toFixed(3)})` }}
            />
          );
        }
        return (
          <span
            key={i}
            className="awv-bar"
            // Stagger each bar's animation start so they don't all peak at once.
            style={{ animationDelay: `${(i % 8) * 0.07}s` }}
          />
        );
      })}
    </div>
  );
}
